import { generateId } from 'ai';
import { isEmpty, orderBy } from 'lodash';
import { create } from 'zustand';

import type {
  StoredThread,
  StoredMessage,
  StoredDiskMessage,
  StoredAttachment,
  StepTimings,
  ThreadCreateInput,
  ThreadUpdateInput,
  MessageCreateInput,
  MessageUpdateInput,
} from '@/lib/assistant-cloud-local/types';
import { encryptJson, hasKey } from '@/lib/history-key/cipher';
import { HistoryLockedError } from '@/lib/history-key/errors';
import { useHistorySession } from '@/lib/history-key/session';
import { popServerMessageId, setRemoteIdForMessage } from '@/lib/utils/message';
import {
  getOrCreateThreadUuid,
  getPendingLocalThreadId,
  clearPendingLocalThreadId,
} from '@/lib/utils/thread';

import * as db from './indexeddb/db';
import {
  openMessage,
  openThreads,
  ownerFromParts,
  sealMessage,
  sealThread,
} from './protected-threads';

function sortMessagesTreeOrder(messages: StoredMessage[]): StoredMessage[] {
  return orderBy(messages, ['height', m => m.created_at.getTime()], ['asc', 'asc']);
}

// A reset or account transition invalidates every pending decrypted result, including A → B → A.
let historyGeneration = 0;
useHistorySession.subscribe((next, previous) => {
  if (next.owner !== previous.owner) historyGeneration++;
});
function assertHistoryGeneration(generation: number): void {
  if (generation !== historyGeneration) throw new HistoryLockedError();
}

let cachedMessagesIndexFor: Map<string, StoredMessage[]> | null = null;
let cachedMessagesIndex: Map<string, StoredMessage> | null = null;

// Index rebuilt when the caller passes a new `messages` Map identity — store
// mutations always spread into a new Map.
export function getMessageById(
  messages: Map<string, StoredMessage[]>,
  messageId: string,
): StoredMessage | undefined {
  if (cachedMessagesIndexFor !== messages || !cachedMessagesIndex) {
    const index = new Map<string, StoredMessage>();
    for (const arr of messages.values()) {
      for (const m of arr) index.set(m.id, m);
    }
    cachedMessagesIndexFor = messages;
    cachedMessagesIndex = index;
  }
  return cachedMessagesIndex.get(messageId);
}

function resetMessagesIndex(): void {
  cachedMessagesIndexFor = null;
  cachedMessagesIndex = null;
}

// ---------------------------------------------------------------------------
// Pending-timing side-channel
//
// The transport observes stream chunks and computes `step_timings` +
// `stream_duration_ms`, but it has no handle to the persisted message — the
// store generates that id later. So the transport stages the data here, keyed
// by `threadId`, and the next persistence write on that thread drains it.
//
// Two assistant-ui flow patterns must work:
//
//   Pattern A — "createMessage early, updateMessage fills":
//     • `createMessage(assistant)` runs at stream-START with empty content.
//       Pending is empty (transport hasn't pushed yet) — nothing stamped.
//     • Stream finalizes → `pushPendingTiming` populates the entry.
//     • `updateMessage(assistant)` runs with the streamed content → drains
//       the pending and stamps the existing record.
//
//   Pattern B — "createMessage after finalize" (this app's actual flow):
//     • Stream finalizes → `pushPendingTiming` populates the entry.
//     • `createMessage(assistant)` runs with the full content → drains the
//       pending and stamps the new record.
//     • Subsequent `updateMessage` calls — including the ones assistant-ui's
//       `CloudMessagePersistence` issues on PREVIOUS assistant messages when
//       a new stream wraps up (state/finishedAt refresh) — must NOT consume.
//       Otherwise the new stream's pending gets drained into the wrong
//       (older) record and the new record never gets stamped.
//
// The compatibility hinge is in `updateMessage`: skip consume when the
// existing record already has `step_timings` / `stream_duration_ms`. Pattern
// A messages haven't been stamped yet → consume fires; pattern B messages
// (including the older-message refreshes) are already stamped → consume is
// skipped. See the inline comment at the call site for the full rationale.
//
// Repeated calls merge into the same pending entry. The 60 s TTL evicts
// orphans if a stream errors before any persistence write drains it.
// ---------------------------------------------------------------------------
const PENDING_TIMING_TTL_MS = 60_000;
type PendingTiming = { durationMs?: number; timings?: StepTimings; createdAt?: number };
const pendingTimings = new Map<string, PendingTiming>();
const pendingTimingEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleEviction(threadId: string): void {
  const existing = pendingTimingEvictionTimers.get(threadId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingTimings.delete(threadId);
    pendingTimingEvictionTimers.delete(threadId);
  }, PENDING_TIMING_TTL_MS);
  pendingTimingEvictionTimers.set(threadId, timer);
}

/**
 * Stages timing data for the next `createMessage` / `updateMessage` on the
 * thread. Any subset of fields can be passed — repeated calls merge into the
 * same pending entry, keyed by `threadId`. Each field is validated
 * independently and silently dropped when invalid:
 *
 *   - `durationMs`: must be finite and >= 0 (0 is valid for cached responses;
 *     negatives indicate clock skew).
 *   - `timings`: must contain at least one step.
 *   - `createdAt`: must be a finite epoch ms. Used by the storybook mock
 *     transport whose synthetic clock is detached from `Date.now()`;
 *     production omits it and relies on the default backdate-from-`durationMs`.
 */
export function pushPendingTiming({
  threadId,
  durationMs,
  timings,
  createdAt,
}: {
  threadId: string;
  durationMs?: number;
  timings?: StepTimings;
  createdAt?: number;
}): void {
  const patch: PendingTiming = {};
  if (durationMs !== undefined && Number.isFinite(durationMs) && durationMs >= 0) {
    patch.durationMs = durationMs;
  }
  if (timings !== undefined && !isEmpty(timings)) {
    patch.timings = timings;
  }
  if (createdAt !== undefined && Number.isFinite(createdAt)) {
    patch.createdAt = createdAt;
  }
  if (isEmpty(patch)) return;
  const current = pendingTimings.get(threadId) ?? {};
  pendingTimings.set(threadId, { ...current, ...patch });
  scheduleEviction(threadId);
}

/**
 * Drains the pending timing entry for `threadId`, returning whatever was
 * staged by `pushPendingTiming`. Single-shot — repeated calls return
 * `undefined` until the next push. Both `createMessage` and `updateMessage`
 * call this for assistant-role messages; only one of them will get a
 * non-empty result per stream (see the module-level docblock for which one
 * fires under each flow pattern).
 *
 * Note: we deliberately do NOT clear the live-trail mirror here. At the
 * running → finished transition, the runtime's message id can briefly lag
 * the persisted id, and clearing would force the trail to fall back to
 * assistant-ui's later `message.createdAt` and shift every row. The
 * persisted-wins merge in `useMergedStepTimings` makes the lingering
 * live-mirror entry harmless; the live-store's own TTL evicts it.
 */
/**
 * A reply cannot predate the question. The backdate below measures the stream from when the request
 * left, which is at or before the moment the user's own message was stamped — so an assistant
 * message could be persisted with a `created_at` a few hundred milliseconds *before* the message it
 * answers, and the thread rendered the reply above the question.
 *
 * Bounded on both sides: never before its parent, and never after `now`, which is its own
 * `updated_at`. Without the upper bound a reply that finished inside the same millisecond as the
 * question would be pushed past its own end and report a negative duration. In the ordinary case —
 * a stream measured in hundreds of milliseconds — neither bound is reached and the duration stands.
 */
function withinParentAndNow({
  createdAt,
  parent,
  now,
}: {
  createdAt: Date;
  parent: StoredMessage | null | undefined;
  now: Date;
}): Date {
  if (!parent) return createdAt;
  const floor = parent.created_at.getTime() + 1;
  const bounded = Math.min(Math.max(createdAt.getTime(), floor), now.getTime());
  return bounded === createdAt.getTime() ? createdAt : new Date(bounded);
}

function consumePendingTiming(threadId: string): PendingTiming | undefined {
  const v = pendingTimings.get(threadId);
  if (v === undefined) return undefined;
  pendingTimings.delete(threadId);
  const timer = pendingTimingEvictionTimers.get(threadId);
  if (timer) {
    clearTimeout(timer);
    pendingTimingEvictionTimers.delete(threadId);
  }
  return v;
}

/**
 * Drops every pending timing entry and cancels its eviction timer. Used by
 * `clear()` so test fixtures don't leak setTimeouts across describe blocks
 * under `jest.useFakeTimers()`, and so a logout doesn't leave timings primed
 * for the next user's first stream.
 */
function clearPendingTimings(): void {
  for (const timer of pendingTimingEvictionTimers.values()) clearTimeout(timer);
  pendingTimingEvictionTimers.clear();
  pendingTimings.clear();
}

function warnFiltered({ reason, ids }: { reason: string; ids: string[] }): void {
  if (ids.length === 0) return;
  console.warn(`[assistant-cloud-store] Filtered ${reason}:`, { count: ids.length, ids });
}

/**
 * Drops messages without ids, duplicate ids, orphans (parent_id → missing
 * message), and parent cycles. Any of these reaching MessageRepository.import
 * throws ("same id already exists in the parent tree" / sparse-array errors), so
 * this is the defensive boundary that keeps a corrupt stored thread from crashing
 * the runtime on load.
 */
export function filterValidMessageChain<T extends StoredDiskMessage>(messages: T[]): T[] {
  if (messages.length === 0) return messages;

  // De-duplicate by id (keep first occurrence). A duplicate id imported twice with
  // a different parent makes MessageRepository link a node under its own ancestor.
  const byId = new Map<string, T>();
  const duplicateIds: string[] = [];
  for (const message of messages) {
    if (!message.id) {
      console.warn('[assistant-cloud-store] Filtering message without ID:', {
        format: message.format,
      });
      continue;
    }
    if (byId.has(message.id)) {
      duplicateIds.push(message.id);
      continue;
    }
    byId.set(message.id, message);
  }

  const validMessages: T[] = [];
  const orphanedIds: string[] = [];
  const cyclicIds: string[] = [];

  for (const message of byId.values()) {
    if (message.parent_id === null) {
      validMessages.push(message);
      continue;
    }

    // Walk the parent chain: it must terminate at a root without revisiting this
    // message (a cycle would make import build a node that is its own ancestor).
    const seen = new Set<string>([message.id]);
    let cyclic = false;
    let ancestor = byId.get(message.parent_id);
    while (ancestor) {
      if (seen.has(ancestor.id)) {
        cyclic = true;
        break;
      }
      seen.add(ancestor.id);
      if (ancestor.parent_id === null) break;
      ancestor = byId.get(ancestor.parent_id);
    }

    if (cyclic) {
      cyclicIds.push(message.id);
    } else if (byId.has(message.parent_id)) {
      validMessages.push(message);
    } else {
      orphanedIds.push(message.id);
    }
  }

  warnFiltered({ reason: 'duplicate-id messages', ids: duplicateIds });
  warnFiltered({ reason: 'orphaned messages', ids: orphanedIds });
  warnFiltered({ reason: 'cyclic messages', ids: cyclicIds });

  return validMessages;
}

/** Disk decides who owns a thread: another tab may have protected it since this one read it.
 *  The write itself checks that owner, so it is tried under the owner in memory first and only a
 *  refusal costs the disk read. No row on disk means another tab deleted the thread: retrying
 *  ownerless would store an account's message in the clear, so the write fails instead. */
async function writeUnderDiskOwner({
  threadId,
  write,
}: {
  threadId: string;
  write: (owner: string | undefined) => Promise<boolean>;
}): Promise<void> {
  if (await write(useAssistantCloudStore.getState().threads.get(threadId)?.owner)) return;
  const onDisk = await db.getThread(threadId);
  if (onDisk && (await write(onDisk.owner))) return;
  throw new Error(`thread ${threadId} changed owner while writing`);
}

function persistMessage({
  message,
  replace,
}: {
  message: StoredMessage;
  replace: boolean;
}): Promise<void> {
  return writeUnderDiskOwner({
    threadId: message.thread_id,
    write: async owner =>
      db.writeMessage({
        message: await sealMessage({ thread: { owner }, message }),
        owner,
        replace,
      }),
  });
}

async function openMessages(messages: StoredDiskMessage[]): Promise<StoredMessage[]> {
  return sortMessagesTreeOrder(
    await Promise.all(filterValidMessageChain(messages).map(openMessage)),
  );
}

async function loadStore() {
  const openedFor = useHistorySession.getState().owner;
  const threads = await openThreads(await db.listThreads({ archived: false }));
  const sortedThreads = sortThreadsByUpdatedAt(threads);
  const threadsMap = sortedThreadsToMap(sortedThreads);

  const messagesMap = new Map<string, StoredMessage[]>();
  for (const thread of sortedThreads) {
    try {
      messagesMap.set(thread.id, await openMessages(await db.listMessages(thread.id)));
    } catch {
      // Messages sealed under a key this session does not hold: the thread stays hidden.
      threadsMap.delete(thread.id);
    }
  }

  const attachments = await db.listAttachments();
  return {
    openedFor,
    threadsMap,
    messagesMap,
    attachmentsMap: new Map(attachments.map(a => [a.id, a])),
  };
}

function sortThreadsByUpdatedAt(threads: StoredThread[]): StoredThread[] {
  return threads.sort((a, b) => {
    const dateA = a.updated_at instanceof Date ? a.updated_at : new Date(a.updated_at);
    const dateB = b.updated_at instanceof Date ? b.updated_at : new Date(b.updated_at);
    return dateB.getTime() - dateA.getTime();
  });
}

function sortedThreadsToMap(threads: StoredThread[]): Map<string, StoredThread> {
  return new Map(threads.map(t => [t.id, t]));
}

interface AssistantCloudState {
  threads: Map<string, StoredThread>;
  messages: Map<string, StoredMessage[]>;
  attachments: Map<string, StoredAttachment>;
  isInitialized: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  initPromise: Promise<void> | null;
  ensureKey: ((owner: string) => Promise<void>) | null;
  /** Whose key the store was last opened with; a different current owner means re-open. */
  openedFor: string | null;

  initialize: () => Promise<void>;
  createThread: (data: ThreadCreateInput) => Promise<string>;
  getThread: (id: string) => StoredThread | undefined;
  getSortedThreads: (options?: { archived?: boolean }) => StoredThread[];
  updateThread: (id: string, updates: ThreadUpdateInput) => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  listThreads: (options?: { archived?: boolean }) => Promise<StoredThread[]>;
  createMessage: (threadId: string, data: MessageCreateInput) => Promise<string>;
  updateMessage: (threadId: string, messageId: string, data: MessageUpdateInput) => Promise<void>;
  getMessage: (id: string) => StoredMessage | undefined;
  listMessages: (threadId: string) => Promise<StoredMessage[]>;
  uploadAttachment: (file: File) => Promise<string>;
  getAttachment: (id: string) => StoredAttachment | undefined;
  clear: () => Promise<void>;
  /** Forgets everything in memory and leaves the database alone; the next initialize re-reads it. */
  reset: () => Promise<void>;
  setSyncing: (syncing: boolean) => void;
}

export const useAssistantCloudStore = create<AssistantCloudState>()((set, get) => ({
  threads: new Map(),
  messages: new Map(),
  attachments: new Map(),
  isInitialized: false,
  isLoading: false,
  isSyncing: false,
  initPromise: null,
  ensureKey: null,
  openedFor: null,

  initialize: async () => {
    const { initPromise, isInitialized } = get();
    if (initPromise) {
      return initPromise;
    }
    if (isInitialized) {
      return Promise.resolve();
    }

    const generation = historyGeneration;
    const promise = (async () => {
      set({ isLoading: true });

      try {
        const loaded = await loadStore();
        assertHistoryGeneration(generation);
        const { openedFor, threadsMap, messagesMap, attachmentsMap } = loaded;

        set({
          threads: threadsMap,
          messages: messagesMap,
          attachments: attachmentsMap,
          isInitialized: true,
          isLoading: false,
          initPromise: null,
          openedFor,
        });
      } catch (error) {
        console.error('Failed to initialize store:', error);
        set({ isLoading: false, initPromise: null });
        throw error;
      }
    })();

    set({ initPromise: promise });
    return promise;
  },

  createThread: async data => {
    const generation = historyGeneration;
    const pendingThreadId = getPendingLocalThreadId();
    const id = pendingThreadId ? getOrCreateThreadUuid(pendingThreadId) : generateId();

    const now = new Date();

    const thread: StoredThread = {
      id,
      external_id: data.external_id ?? null,
      title: data.title || '',
      is_archived: false,
      created_at: now,
      updated_at: now,
      last_message_at: data.last_message_at || now,
    };

    set({ isSyncing: true });

    try {
      await db.createThread(thread);
      assertHistoryGeneration(generation);

      set(state => {
        const threadsArray = [thread, ...Array.from(state.threads.values())];
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        return {
          threads: sortedThreadsToMap(sortedThreads),
          messages: new Map(state.messages).set(id, []),
          isSyncing: false,
        };
      });

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    } finally {
      if (pendingThreadId && getPendingLocalThreadId() === pendingThreadId) {
        clearPendingLocalThreadId();
      }
    }
  },

  getThread: id => get().threads.get(id),

  getSortedThreads: options => {
    const { threads } = get();
    const threadsArray = Array.from(threads.values());
    const filtered =
      options?.archived !== undefined
        ? threadsArray.filter(t => t.is_archived === options.archived)
        : threadsArray;
    return sortThreadsByUpdatedAt([...filtered]);
  },

  updateThread: async (id, updates) => {
    const generation = historyGeneration;
    const { threads } = get();
    const thread = threads.get(id);
    if (!thread) {
      throw new Error(`Thread not found: ${id}`);
    }

    set({ isSyncing: true });

    try {
      await writeUnderDiskOwner({
        threadId: id,
        write: async owner =>
          db.writeThread({ id, updates: await sealTitleUpdate({ owner, updates, id }), owner }),
      });

      assertHistoryGeneration(generation);
      set(state => {
        const updatedThread = { ...thread, ...updates, updated_at: new Date() };
        const threadsArray = Array.from(state.threads.values()).map(t =>
          t.id === id ? updatedThread : t,
        );
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        return {
          threads: sortedThreadsToMap(sortedThreads),
          isSyncing: false,
        };
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  deleteThread: async id => {
    set({ isSyncing: true });

    try {
      await db.deleteThread(id);

      set(state => {
        const threads = new Map(state.threads);
        const messages = new Map(state.messages);
        threads.delete(id);
        messages.delete(id);
        return { threads, messages, isSyncing: false };
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  listThreads: async options => {
    const generation = historyGeneration;
    const threads = await openThreads(await db.listThreads(options));
    const sortedThreads = sortThreadsByUpdatedAt(threads);

    assertHistoryGeneration(generation);
    set({ threads: sortedThreadsToMap(sortedThreads) });

    return threads;
  },

  createMessage: async (threadId, data) => {
    const owner = ownerFromParts(data.content);
    if (owner) await protectThread({ threadId, owner });
    const generation = historyGeneration;
    const content = data.content as { role?: string } | undefined;
    const isAssistantMessage = content?.role === 'assistant';
    const serverMessageId = isAssistantMessage ? popServerMessageId(threadId) : null;
    const id = data.id || generateId();
    const now = new Date();
    const { messages } = get();
    const threadMessages = messages.get(threadId) || [];
    const parentMessage = data.parent_id ? threadMessages.find(m => m.id === data.parent_id) : null;
    const height = parentMessage ? parentMessage.height + 1 : 0;

    if (serverMessageId) {
      setRemoteIdForMessage(id, serverMessageId);
    }

    // Backdate so the trail header shows the real stream duration. Transport
    // `flush()` runs before this, so duration + step timings are already stashed.
    let createdAt = now;
    let streamDurationMs: number | undefined;
    let stepTimings: StepTimings | undefined;
    if (isAssistantMessage) {
      const pending = consumePendingTiming(threadId);
      if (pending?.createdAt !== undefined) {
        createdAt = new Date(pending.createdAt);
        streamDurationMs = pending?.durationMs;
      } else if (pending?.durationMs !== undefined) {
        createdAt = new Date(now.getTime() - pending.durationMs);
        streamDurationMs = pending.durationMs;
      }
      stepTimings = pending?.timings;
    }

    const message: StoredMessage = {
      id,
      remote_id: serverMessageId,
      thread_id: threadId,
      parent_id: data.parent_id ?? null,
      height,
      format: data.format,
      content: data.content,
      created_at: withinParentAndNow({ createdAt, parent: parentMessage, now }),
      updated_at: now,
      ...(streamDurationMs !== undefined && { stream_duration_ms: streamDurationMs }),
      ...(stepTimings && { step_timings: stepTimings }),
    };

    // assistant-ui can re-create a message under the same id mid-run (id-swap
    // path, react-ai-sdk #4037). A fresh message is an IndexedDB `add` that
    // rejects duplicate keys, so a replacement must upsert (put) to actually
    // persist — otherwise it silently reverts on reload.
    const isReplacement = threadMessages.some(m => m.id === id);

    set({ isSyncing: true });

    try {
      await persistMessage({ message, replace: isReplacement });
      await db.updateThread(threadId, { last_message_at: now });
      assertHistoryGeneration(generation);

      set(state => {
        const thread = state.threads.get(threadId);
        if (!thread) {
          return { isSyncing: false };
        }

        const updatedThread = {
          ...thread,
          last_message_at: now,
          updated_at: now,
        };

        const threadsArray = Array.from(state.threads.values()).map(t =>
          t.id === threadId ? updatedThread : t,
        );
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        // Derive from current state (not the pre-await snapshot, which a concurrent
        // write could have superseded) and replace any existing entry for this id.
        const current = state.messages.get(threadId) ?? [];
        const existingIndex = current.findIndex(m => m.id === id);
        const nextThreadMessages =
          existingIndex === -1
            ? [...current, message]
            : current.map((m, i) => (i === existingIndex ? message : m));

        return {
          messages: new Map(state.messages).set(threadId, nextThreadMessages),
          threads: sortedThreadsToMap(sortedThreads),
          isSyncing: false,
        };
      });

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  updateMessage: async (threadId, messageId, data) => {
    const owner = ownerFromParts(data.content);
    if (owner) await protectThread({ threadId, owner });
    const generation = historyGeneration;
    const { messages } = get();
    const threadMessages = messages.get(threadId) || [];
    const existing = threadMessages.find(m => m.id === messageId);
    if (!existing) return;
    const parentOfExisting = existing.parent_id
      ? threadMessages.find(m => m.id === existing.parent_id)
      : null;

    const now = new Date();
    // `alreadyStamped` is the load-bearing guard for the pending-timing
    // side-channel — see the module-level docblock above `pendingTimings`
    // for the full pattern-A vs pattern-B rationale. tl;dr: an
    // already-stamped assistant message is either pattern B's just-created
    // record (already drained pending in createMessage) or a *previous*
    // assistant message that assistant-ui is refreshing because a NEW
    // stream just finalized — consuming here would steal the new stream's
    // pending and stamp it onto the wrong record.
    const isAssistantMessage =
      (existing.content as { role?: string } | undefined)?.role === 'assistant';
    const alreadyStamped =
      existing.step_timings !== undefined || existing.stream_duration_ms !== undefined;
    let createdAtUpdate: Date | undefined;
    let streamDurationMsUpdate: number | undefined;
    let stepTimingsUpdate: StepTimings | undefined;
    if (isAssistantMessage && !alreadyStamped) {
      const pending = consumePendingTiming(threadId);
      if (pending?.createdAt !== undefined) {
        createdAtUpdate = new Date(pending.createdAt);
        streamDurationMsUpdate = pending.durationMs;
      } else if (pending?.durationMs !== undefined) {
        createdAtUpdate = new Date(now.getTime() - pending.durationMs);
        streamDurationMsUpdate = pending.durationMs;
      }
      if (pending?.timings) stepTimingsUpdate = pending.timings;
    }

    const updated: StoredMessage = {
      ...existing,
      ...(data.content !== undefined && { content: data.content }),
      ...(data.format !== undefined && { format: data.format }),
      ...(createdAtUpdate && {
        created_at: withinParentAndNow({
          createdAt: createdAtUpdate,
          parent: parentOfExisting,
          now,
        }),
      }),
      ...(streamDurationMsUpdate !== undefined && { stream_duration_ms: streamDurationMsUpdate }),
      ...(stepTimingsUpdate && { step_timings: stepTimingsUpdate }),
      updated_at: now,
    };

    set({ isSyncing: true });
    try {
      await persistMessage({ message: updated, replace: true });
      assertHistoryGeneration(generation);
      set(state => ({
        messages: new Map(state.messages).set(
          threadId,
          (state.messages.get(threadId) || []).map(m => (m.id === messageId ? updated : m)),
        ),
        isSyncing: false,
      }));
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  getMessage: id => getMessageById(get().messages, id),

  // Format filtering happens in MessagesAPI.list() — it normalizes the format
  // so FormattedCloudPersistence can decode regardless of stored format.
  listMessages: async threadId => {
    const generation = historyGeneration;
    const { messages, threads } = get();
    const thread = threads.get(threadId) ?? (await db.getThread(threadId));
    if (thread?.owner && !hasKey(thread.owner)) throw new HistoryLockedError();
    assertHistoryGeneration(generation);
    const cachedMessages = messages.get(threadId);

    if (cachedMessages?.length) {
      return cachedMessages;
    }

    const sortedMessages = await openMessages(await db.listMessages(threadId));
    assertHistoryGeneration(generation);

    set(state => ({
      messages: new Map(state.messages).set(threadId, sortedMessages),
    }));

    return sortedMessages;
  },

  uploadAttachment: async file => {
    const id = generateId();
    const attachment: StoredAttachment = {
      id,
      name: file.name,
      content_type: file.type,
      size: file.size,
      data: file,
      created_at: new Date(),
    };

    set({ isSyncing: true });

    try {
      await db.createAttachment(attachment);

      set(state => ({
        attachments: new Map(state.attachments).set(id, attachment),
        isSyncing: false,
      }));

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  getAttachment: id => get().attachments.get(id),

  clear: async () => {
    historyGeneration++;
    set({ isSyncing: true });

    try {
      await db.clearDB();

      resetMessagesIndex();
      clearPendingTimings();
      set({
        threads: new Map(),
        messages: new Map(),
        attachments: new Map(),
        isSyncing: false,
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  reset: async () => {
    historyGeneration++;
    // A pending load is invalidated above; cleanup must still run when it rejects.
    await get().initPromise?.catch(() => undefined);
    resetMessagesIndex();
    set({
      threads: new Map(),
      messages: new Map(),
      attachments: new Map(),
      isInitialized: false,
      initPromise: null,
    });
  },

  setSyncing: syncing => set({ isSyncing: syncing }),
}));

async function sealTitleUpdate({
  owner,
  updates,
  id,
}: {
  owner: string | undefined;
  updates: ThreadUpdateInput;
  id: string;
}): Promise<ThreadUpdateInput & Pick<StoredThread, 'title_enc'>> {
  if (!owner || updates.title === undefined) return updates;
  const title_enc = await encryptJson({ owner, value: updates.title, context: [id] });
  return { ...updates, title: '', title_enc };
}

/** Marks the thread as belonging to `owner` and encrypts what it already holds on disk.
 *  Throws when the key cannot be obtained or the rewrite fails, so nothing protected is ever
 *  left in the clear while the thread counts as owned. A thread another account already owns
 *  is never handed over; the server refuses such turns and the lock hides the thread. */
async function protectThread({ threadId, owner }: { threadId: string; owner: string }) {
  const { threads, ensureKey } = useAssistantCloudStore.getState();
  if (!ensureKey) throw new Error('history key resolver is unavailable');
  await ensureKey(owner);
  const generation = historyGeneration;
  const thread = (await db.getThread(threadId)) ?? threads.get(threadId);
  assertHistoryGeneration(generation);
  if (!thread) throw new Error(`unknown thread ${threadId}`);
  if (thread.owner === owner) return;
  if (thread.owner) throw new Error(`thread ${threadId} belongs to another account`);
  const owned = { ...thread, owner };
  const sealed = await db.rewriteThread({
    threadId,
    mapThread: stored => {
      if (stored.owner && stored.owner !== owner) throw new Error('claimed by another account');
      return stored.owner === owner ? Promise.resolve(stored) : sealThread({ ...stored, owner });
    },
    mapMessage: message => sealMessage({ thread: owned, message }),
  });
  if (!sealed) throw new Error(`could not encrypt thread ${threadId}`);
  assertHistoryGeneration(generation);
  useAssistantCloudStore.setState(state => ({
    threads: new Map(state.threads).set(threadId, owned),
  }));
}
